import { getWebTelemetry, initWebTelemetry } from '@viewlift/telemetry-sdk/web';
import Cookies from 'js-cookie';
import { getTelemetryIngestEndpoint, readTelemetryPlatform, readTelemetryRequestId } from './shakaTelemetryIntegration';

type AppWindow = Window & {
  env?: string;
  fireGaEvents?: boolean;
  app_data?: {
    appcmsMain?: {
      id?: string | number;
      site?: string;
    };
    site?: {
      siteInternalName?: string;
    };
  };
};

const WEB_SESSION_IDLE_MS = 30 * 60 * 1000;

export function resolveWebSessionId(storage: Storage, siteKey: string, now = Date.now()): string {
  const key = `vl-web-session:${siteKey || 'site'}`;
  try {
    const raw = storage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string; seen?: number };
      if (parsed.id && typeof parsed.seen === 'number' && now - parsed.seen < WEB_SESSION_IDLE_MS) {
        storage.setItem(key, JSON.stringify({ id: parsed.id, seen: now }));
        return parsed.id;
      }
    }
  } catch {
    /* private mode or a bad stored value */
  }
  const id = `web_${now.toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  try {
    storage.setItem(key, JSON.stringify({ id, seen: now }));
  } catch {
    /* private mode */
  }
  return id;
}

/** Full trace sample on a developer machine. Production stays on the SDK default (0.1). */
export function telemetrySampleRatio(environment: string): number | undefined {
  const env = environment.toLowerCase();
  if (env === 'localhost' || env === 'development' || env === 'dev' || env === 'test') return 1;
  return undefined;
}

let started = false;

const readWindow = (): AppWindow | undefined =>
  typeof window !== 'undefined' ? (window as AppWindow) : undefined;

/** Same gate as GA: performance cookie, or OneTrust/Ketch already flipped `fireGaEvents`. */
export function hasTelemetryConsent(): boolean {
  const w = readWindow();
  if (!w) return false;
  const env = String(w.env || '').toLowerCase();
  if (env === 'localhost' || env === 'development' || env === 'dev') return true;
  if (w.fireGaEvents) return true;
  const raw = Cookies.get('cc_cookie');
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { level?: string[] };
    return Boolean(parsed?.level?.includes('performanceCookie'));
  } catch {
    return false;
  }
}

/**
 * Start the site collector once consent is present.
 * Returns true when a collector is active.
 */
export function syncWebTelemetry(): boolean {
  if (started) return true;
  if (!hasTelemetryConsent()) return false;
  const w = readWindow();
  const siteIdRaw = w?.app_data?.appcmsMain?.id;
  const siteRaw = w?.app_data?.site?.siteInternalName || w?.app_data?.appcmsMain?.site;
  const siteId = siteIdRaw != null && String(siteIdRaw).trim() !== '' ? String(siteIdRaw).trim() : undefined;
  const site = siteRaw != null && String(siteRaw).trim() !== '' ? String(siteRaw).trim() : undefined;
  const environment = w?.env && String(w.env).trim() !== '' ? String(w.env).trim() : 'production';
  const sessionId =
    typeof sessionStorage !== 'undefined' ? resolveWebSessionId(sessionStorage, siteId || site || 'site') : undefined;

  const endpoint = getTelemetryIngestEndpoint();
  if (!endpoint) return false;

  const sampleRatio = telemetrySampleRatio(environment);
  const requestId = readTelemetryRequestId();
  initWebTelemetry({
    endpoint,
    serviceName: 'website',
    serviceVersion: 'webapp-v2',
    deploymentEnvironment: environment,
    platform: readTelemetryPlatform(),
    ...(requestId != null ? { requestId } : {}),
    ...(siteId != null ? { siteId, tenantId: siteId } : {}),
    ...(site != null ? { site } : {}),
    ...(sessionId != null ? { sessionId } : {}),
    ...(sampleRatio != null ? { sampleRatio } : {}),
    consent: true,
  });
  started = true;
  return true;
}

/** Record the current SPA path. The SDK templates ids and strips query strings. */
export function trackWebRoute(pathname: string): void {
  if (!syncWebTelemetry()) return;
  const path = pathname && pathname.trim() !== '' ? pathname : '/';
  getWebTelemetry()?.route(path);
}

/** Consent just became true (cookie, Ketch, or OneTrust). */
export function enableWebTelemetry(): void {
  trackWebRoute(readWindow()?.location?.pathname || '/');
}
